import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import type { ReactNode } from "react";
import WelcomePage from "./pages/WelcomePage";
import RegistrationPage from "./pages/RegistrationPage";
import VerificationPage from "./pages/VerificationPage";
import PasswordPage from "./pages/PasswordPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import AuthCallbackPage from "./pages/AuthCallbackPage";
import OnboardingPage from "./pages/OnboardingPage";
import OverviewLoadingPage from "./pages/OverviewLoadingPage";
import OverviewPage from "./pages/OverviewPage";
import FindingsPage from "./pages/FindingsPage";
import HypotesesPage from "./pages/HypotesesPage";
import GraphPage from "./pages/GraphPage";
import FeedPage from "./pages/FeedPage";
import ReportsPage from "./pages/ReportsPage";
import SettingsPage from "./pages/SettingsPage";
import { AuthProvider } from "./auth";
import { useAuth } from "./AuthContext";
import { AppStateProvider } from "./AppState";
import { useAppState } from "./AppStateContext";
import { FullscreenLoader } from "./components/FullscreenLoader";
import { ScanIntroOverlay } from "./components/ScanIntroOverlay";
import { CookieConsent } from "./components/CookieConsent";
import { TourProvider } from "./tour/TourProvider";
import { DemoBanner } from "./demo/DemoBanner";
import { DemoAlarm } from "./demo/DemoAlarm";
import { LangProvider, useI18n } from "./i18n";

function RequireAuth({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  const { initialized } = useAppState();
  const location = useLocation();
  const { t } = useI18n();
  if (loading) {
    return <FullscreenLoader label={t("common.loading")} />;
  }
  if (!user) {
    return <Navigate to="/" replace state={{ from: location.pathname }} />;
  }
  if (!initialized) {
    return <FullscreenLoader label={t("common.loading")} />;
  }
  return <>{children}</>;
}

export default function App() {
  return (
    <LangProvider>
      <AuthProvider>
        <AppStateProvider>
          <BrowserRouter>
          <TourProvider>
          <DemoBanner />
          <DemoAlarm />
          <Routes>
            <Route path="/" element={<WelcomePage />} />
            <Route path="/signup" element={<RegistrationPage />} />
            <Route path="/verify" element={<VerificationPage />} />
            <Route path="/password" element={<PasswordPage />} />
            {/* Recovery email links here; PasswordPage reads ?token= and enters reset mode. */}
            <Route path="/auth/reset-password" element={<PasswordPage />} />
            <Route path="/forgot" element={<ForgotPasswordPage />} />
            <Route path="/auth/callback" element={<AuthCallbackPage />} />
            <Route
              path="/onboarding"
              element={
                <RequireAuth>
                  <OnboardingPage />
                </RequireAuth>
              }
            />
            <Route
              path="/overview/loading"
              element={
                <RequireAuth>
                  <OverviewLoadingPage />
                </RequireAuth>
              }
            />
            <Route
              path="/overview"
              element={
                <RequireAuth>
                  <OverviewPage />
                </RequireAuth>
              }
            />
            <Route
              path="/overview/paused"
              element={
                <RequireAuth>
                  <OverviewPage initialStatus="paused" />
                </RequireAuth>
              }
            />
            <Route
              path="/overview/chat"
              element={
                <RequireAuth>
                  <OverviewPage initialChatOpen />
                </RequireAuth>
              }
            />
            <Route
              path="/findings"
              element={
                <RequireAuth>
                  <FindingsPage />
                </RequireAuth>
              }
            />
            <Route
              path="/hypoteses"
              element={
                <RequireAuth>
                  <HypotesesPage />
                </RequireAuth>
              }
            />
            <Route
              path="/graph"
              element={
                <RequireAuth>
                  <GraphPage />
                </RequireAuth>
              }
            />
            <Route
              path="/feed"
              element={
                <RequireAuth>
                  <FeedPage />
                </RequireAuth>
              }
            />
            <Route
              path="/reports"
              element={
                <RequireAuth>
                  <ReportsPage />
                </RequireAuth>
              }
            />
            <Route
              path="/settings"
              element={
                <RequireAuth>
                  <SettingsPage />
                </RequireAuth>
              }
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </TourProvider>
          </BrowserRouter>
          <ScanIntroOverlay />
          <CookieConsent />
        </AppStateProvider>
      </AuthProvider>
    </LangProvider>
  );
}
